import { create } from 'zustand'
import type { GapLabelEdit, TextDetail } from '../types'
import {
  draftFrom, shiftCardMarks, type CardDeckChoice, type DraftNewDeck, type EditCardMark, type EditDraft,
} from '../utils/textEdit'

/**
 * A text's edit-mode draft (handoff §15): everything the learner has changed and not
 * yet saved. In memory, keyed to one text, so going to the word picker and back keeps
 * it; leaving edit mode (saved or discarded) clears it. Nothing here reaches the
 * server until the one PATCH.
 */

export type EditTab = 'text' | 'gaps' | 'decks'

interface TextEditState {
  textId: string | null
  tab: EditTab
  draft: EditDraft | null
  /** Gap forms the word picker could not draw (an occurrence off a word boundary): they ride through it unchanged. */
  pickerCarried: string[]

  /** Starts a draft for *text*; a draft already open for the same text is kept. */
  begin: (text: TextDetail) => void
  setTab: (tab: EditTab) => void
  setPickerCarried: (forms: string[]) => void
  setTitle: (title: string) => void
  setBody: (body: string) => void
  setForms: (forms: string[]) => void
  /** «Убрать пропуск»: the gap's form leaves the wanted set. */
  removeForm: (form: string) => void
  linkDeck: (deck: { id: string; title: string }) => void
  unlinkDeck: (id: string) => void
  /** «Привязать колоду» → «Новая колода»: a deck the save makes and links (Amendment 4 Decision 3). */
  linkNewDeck: () => void
  unlinkNewDeck: () => void
  /** The new deck's name (null: the text's title) or queue switch. */
  setNewDeck: (change: Partial<Pick<DraftNewDeck, 'title' | 'isActive'>>) => void
  /** Drop a waiting word from the queue (an unmarked card word). */
  dropWaiting: (id: string) => void
  /** The picker's card marks, replacing the draft's. */
  setCardMarks: (marks: EditCardMark[]) => void
  /** Unmark one card word: only the mark goes; no card is touched (Amendment 3 Decision 5). */
  removeCardMark: (start: number) => void
  setCardDeck: (choice: CardDeckChoice) => void
  setLabelEdit: (form: string, edit: GapLabelEdit | null) => void
  addAnswer: (form: string, answer: string) => void
  /** Remove an accepted answer: a saved one by id, one added in this draft by its text. */
  removeAnswer: (form: string, target: { id: string } | { added: string }) => void
  reset: () => void
}

const NEW_DECK: DraftNewDeck = { title: null, isActive: true, linked: false }

const update = (set: (fn: (s: TextEditState) => Partial<TextEditState>) => void, change: (d: EditDraft) => EditDraft) =>
  set((s) => (s.draft ? { draft: change(s.draft) } : s))

export const useTextEditStore = create<TextEditState>((set) => ({
  textId: null,
  tab: 'text',
  draft: null,
  pickerCarried: [],

  begin: (text) =>
    set((s) => (s.textId === text.id && s.draft ? s : { textId: text.id, tab: 'text', draft: draftFrom(text) })),
  setTab: (tab) => set({ tab }),
  setPickerCarried: (pickerCarried) => set({ pickerCarried }),
  setTitle: (title) => update(set, (d) => ({ ...d, title })),
  // A card mark follows the typing; one the edit touched is dropped.
  setBody: (body) => update(set, (d) => ({ ...d, body, cardMarks: shiftCardMarks(d.cardMarks, d.body, body) })),
  setForms: (forms) => update(set, (d) => ({ ...d, forms })),
  removeForm: (form) => update(set, (d) => ({ ...d, forms: d.forms.filter((f) => f !== form) })),
  linkDeck: (deck) =>
    update(set, (d) => (d.deckIds.includes(deck.id) ? d : {
      ...d, deckIds: [...d.deckIds, deck.id], deckTitles: { ...d.deckTitles, [deck.id]: deck.title },
    })),
  unlinkDeck: (id) => update(set, (d) => ({
    ...d,
    deckIds: d.deckIds.filter((x) => x !== id),
    cardDeck: d.cardDeck?.kind === 'existing_deck' && d.cardDeck.id === id ? null : d.cardDeck,
  })),
  linkNewDeck: () => update(set, (d) => ({ ...d, newDeck: { ...(d.newDeck ?? NEW_DECK), linked: true } })),
  unlinkNewDeck: () => update(set, (d) => ({
    ...d, newDeck: null, cardDeck: d.cardDeck?.kind === 'new_deck' ? null : d.cardDeck,
  })),
  setNewDeck: (change) => update(set, (d) => ({ ...d, newDeck: { ...(d.newDeck ?? NEW_DECK), ...change } })),
  dropWaiting: (id) => update(set, (d) => (d.dropWaiting.includes(id) ? d : { ...d, dropWaiting: [...d.dropWaiting, id] })),
  setCardMarks: (cardMarks) => update(set, (d) => ({ ...d, cardMarks })),
  removeCardMark: (start) => update(set, (d) => ({ ...d, cardMarks: d.cardMarks.filter((m) => m.start !== start) })),
  // A name typed for a new deck that was only the cards' deck goes with that choice.
  setCardDeck: (cardDeck) => update(set, (d) => ({
    ...d, cardDeck, newDeck: cardDeck.kind === 'existing_deck' && !d.newDeck?.linked ? null : d.newDeck,
  })),
  setLabelEdit: (form, edit) =>
    update(set, (d) => {
      const rest = { ...d.labelEdits }
      delete rest[form]
      return { ...d, labelEdits: edit ? { ...rest, [form]: edit } : rest }
    }),
  addAnswer: (form, answer) =>
    update(set, (d) => {
      const current = d.alternativeEdits[form] ?? { add: [], remove: [] }
      if (current.add.includes(answer)) return d
      return { ...d, alternativeEdits: { ...d.alternativeEdits, [form]: { ...current, add: [...current.add, answer] } } }
    }),
  removeAnswer: (form, target) =>
    update(set, (d) => {
      const current = d.alternativeEdits[form] ?? { add: [], remove: [] }
      const next = 'id' in target
        ? { ...current, remove: current.remove.includes(target.id) ? current.remove : [...current.remove, target.id] }
        : { ...current, add: current.add.filter((a) => a !== target.added) }
      return { ...d, alternativeEdits: { ...d.alternativeEdits, [form]: next } }
    }),
  reset: () => set({ textId: null, tab: 'text', draft: null, pickerCarried: [] }),
}))
